import Pusher from "pusher-js"

interface IncomingCallData {
  type: "incoming_call"
  request_id: number
  caller_id: number
  caller_name: string
  channel_name: string
  expires_at: string
}

interface RequestAcceptedData {
  type: "request_accepted"
  request_id: number
  callee_id: number
  callee_name: string
  channel_name: string
}

interface RequestRejectedData {
  type: "request_rejected"
  request_id: number
  callee_id: number
  callee_name: string
}

interface WebRTCSignalData {
  type: string
  sender_id: number
  sender_name: string
  data: any
}

class VoiceNotificationManager {
  pusher: Pusher | null = null
  channel: any = null
  notificationContainer: HTMLElement | null = null
  currentNotification: HTMLElement | null = null
  countdownInterval: number | null = null
  ringtoneInterval: number | null = null
  userId: number | null = null

  connect() {
    const userMeta = document.querySelector('meta[name="current-user-id"]')
    const pusherKeyMeta = document.querySelector('meta[name="pusher-key"]')
    const pusherClusterMeta = document.querySelector('meta[name="pusher-cluster"]')

    if (!userMeta || !pusherKeyMeta) {
      console.log("[VoiceNotification] Missing user or Pusher config")
      return
    }

    this.userId = parseInt(userMeta.getAttribute("content") || "0")
    const pusherKey = pusherKeyMeta.getAttribute("content") || ""
    const pusherCluster = pusherClusterMeta?.getAttribute("content") || "ap3"

    if (!this.userId || !pusherKey) {
      console.log("[VoiceNotification] Invalid user ID or Pusher key")
      return
    }

    console.log("[VoiceNotification] Connecting to Pusher...")

    this.pusher = new Pusher(pusherKey, {
      cluster: pusherCluster,
      authEndpoint: "/pusher/auth",
      auth: {
        headers: {
          "X-CSRF-Token": this.getCSRFToken()
        }
      }
    })

    // Private 채널 사용 (인증 필요)
    this.channel = this.pusher.subscribe(`private-voice-notifications-${this.userId}`)

    this.channel.bind("pusher:subscription_succeeded", () => {
      console.log("[VoiceNotification] Connected successfully!")
    })

    this.channel.bind("pusher:subscription_error", (error: any) => {
      console.log("[VoiceNotification] Connection error:", error)
    })

    // Bind to events
    this.channel.bind("incoming-call", (data: IncomingCallData) => {
      console.log("[VoiceNotification] Received incoming call:", data)
      this.showIncomingCallNotification(data)
    })

    this.channel.bind("request-accepted", (data: RequestAcceptedData) => {
      console.log("[VoiceNotification] Request accepted:", data)
      this.handleRequestAccepted(data)
    })

    this.channel.bind("request-rejected", (data: RequestRejectedData) => {
      console.log("[VoiceNotification] Request rejected:", data)
      this.showRejectedNotification(data)
    })

    this.channel.bind("webrtc-offer", (data: WebRTCSignalData) => {
      console.log("[VoiceNotification] Received WebRTC offer")
      this.handleWebRTCOffer(data)
    })

    this.channel.bind("webrtc-answer", (data: WebRTCSignalData) => {
      console.log("[VoiceNotification] Received WebRTC answer")
      this.handleWebRTCAnswer(data)
    })

    this.channel.bind("webrtc-ice-candidate", (data: WebRTCSignalData) => {
      console.log("[VoiceNotification] Received ICE candidate")
      this.handleWebRTCIceCandidate(data)
    })

    this.channel.bind("webrtc-hangup", (data: WebRTCSignalData) => {
      console.log("[VoiceNotification] Received hangup")
      this.handleWebRTCHangup(data)
    })

    this.createNotificationContainer()
  }

  disconnect() {
    if (this.channel) {
      this.channel.unbind_all()
      this.pusher?.unsubscribe(`private-voice-notifications-${this.userId}`)
    }
    if (this.pusher) {
      this.pusher.disconnect()
    }
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval)
    }
  }

  createNotificationContainer() {
    if (document.getElementById("voice-notification-container")) return

    const container = document.createElement("div")
    container.id = "voice-notification-container"
    container.style.cssText = "position: fixed; top: 16px; right: 16px; z-index: 9999; display: flex; flex-direction: column; gap: 8px;"
    document.body.appendChild(container)
    this.notificationContainer = container
  }

  showIncomingCallNotification(data: IncomingCallData) {
    this.clearCurrentNotification()

    const expiresAt = new Date(data.expires_at)

    const notification = document.createElement("div")
    notification.id = `voice-notification-${data.request_id}`
    notification.style.cssText = "background: linear-gradient(to right, #4f46e5, #7c3aed); color: white; border-radius: 16px; padding: 24px; max-width: 350px; box-shadow: 0 25px 50px rgba(0,0,0,0.5); border: 1px solid rgba(99, 102, 241, 0.3);"
    notification.innerHTML = `
      <div style="display: flex; align-items: flex-start; gap: 16px;">
        <div style="flex-shrink: 0;">
          <div style="width: 56px; height: 56px; background: rgba(255,255,255,0.2); border-radius: 50%; display: flex; align-items: center; justify-content: center;">
            <svg style="width: 32px; height: 32px; color: white;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
            </svg>
          </div>
        </div>
        <div style="flex: 1; min-width: 0;">
          <p style="font-size: 18px; font-weight: bold; margin: 0;">음성 연결 요청</p>
          <p style="font-size: 14px; color: #c7d2fe; margin-top: 4px;">
            <span style="font-weight: 600;">${this.escapeHtml(data.caller_name)}</span>님이
          </p>
          <p style="font-size: 14px; color: #c7d2fe;">
            <span style="font-weight: 500; color: #fde047;">${this.escapeHtml(data.channel_name)}</span> 채널로 초대합니다
          </p>
          <p style="font-size: 12px; color: #a5b4fc; margin-top: 8px;">
            <span id="countdown-${data.request_id}" style="font-family: monospace; background: rgba(255,255,255,0.1); padding: 2px 8px; border-radius: 4px;">30</span>초 후 만료
          </p>
        </div>
      </div>
      <div style="display: flex; gap: 12px; margin-top: 16px;">
        <button onclick="window.voiceNotificationManager.respondToCall(${data.request_id}, true)"
                style="flex: 1; background: #10b981; color: white; font-weight: bold; padding: 12px 16px; border-radius: 12px; border: none; cursor: pointer;">
          수락
        </button>
        <button onclick="window.voiceNotificationManager.respondToCall(${data.request_id}, false)"
                style="flex: 1; background: rgba(239, 68, 68, 0.8); color: white; font-weight: bold; padding: 12px 16px; border-radius: 12px; border: none; cursor: pointer;">
          거절
        </button>
      </div>
    `

    this.notificationContainer?.appendChild(notification)
    this.currentNotification = notification
    this.startCountdown(data.request_id, expiresAt)
    this.playNotificationSound()
  }

  startCountdown(requestId: number, expiresAt: Date) {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval)
    }

    const countdownEl = document.getElementById(`countdown-${requestId}`)

    this.countdownInterval = window.setInterval(() => {
      const remaining = Math.max(0, Math.floor((expiresAt.getTime() - Date.now()) / 1000))

      if (countdownEl) {
        countdownEl.textContent = remaining.toString()
      }

      if (remaining <= 0) {
        this.clearCurrentNotification()
        this.showToast("연결 요청이 만료되었습니다", "warning")
      }
    }, 1000)
  }

  handleRequestAccepted(data: RequestAcceptedData) {
    this.showToast(`${data.callee_name}님이 연결 요청을 수락했습니다!`, "success")
    const webRTCManager = (window as any).webRTCManager
    if (webRTCManager) {
      webRTCManager.startCall(data.callee_id, data.callee_name)
    }
  }

  showRejectedNotification(data: RequestRejectedData) {
    this.showToast(`${data.callee_name}님이 연결 요청을 거절했습니다`, "error")
  }

  handleWebRTCOffer(data: WebRTCSignalData) {
    const webRTCManager = (window as any).webRTCManager
    if (webRTCManager) {
      webRTCManager.handleOffer(data.data, data.sender_id, data.sender_name)
    }
  }

  handleWebRTCAnswer(data: WebRTCSignalData) {
    const webRTCManager = (window as any).webRTCManager
    if (webRTCManager) {
      webRTCManager.handleAnswer(data.data)
    }
  }

  handleWebRTCIceCandidate(data: WebRTCSignalData) {
    const webRTCManager = (window as any).webRTCManager
    if (webRTCManager) {
      webRTCManager.handleIceCandidate(data.data)
    }
  }

  handleWebRTCHangup(data: WebRTCSignalData) {
    const webRTCManager = (window as any).webRTCManager
    if (webRTCManager) {
      webRTCManager.handleHangup()
    }
    this.showToast(`${data.sender_name}님이 연결을 종료했습니다`, "info")
  }

  showToast(message: string, type: "success" | "error" | "warning" | "info") {
    const bgColors: Record<string, string> = {
      success: "#059669",
      error: "#dc2626",
      warning: "#d97706",
      info: "#2563eb"
    }

    const toast = document.createElement("div")
    toast.style.cssText = `background: ${bgColors[type] || bgColors.info}; color: white; padding: 12px 16px; border-radius: 12px; box-shadow: 0 10px 25px rgba(0,0,0,0.3); max-width: 320px; font-weight: 500; margin-bottom: 8px;`
    toast.textContent = this.escapeHtml(message)

    this.notificationContainer?.appendChild(toast)

    setTimeout(() => {
      toast.remove()
    }, 5000)
  }

  async respondToCall(requestId: number, accepted: boolean) {
    this.clearCurrentNotification()

    try {
      const response = await fetch("/voice/respond", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.getCSRFToken()
        },
        body: JSON.stringify({ request_id: requestId, accepted })
      })

      const data = await response.json()

      if (data.success) {
        if (accepted) {
          this.showToast("연결 요청을 수락했습니다", "success")
        } else {
          this.showToast("연결 요청을 거절했습니다", "info")
        }
      } else {
        this.showToast(data.error || "오류가 발생했습니다", "error")
      }
    } catch (error) {
      console.error("Failed to respond to call:", error)
      this.showToast("요청 처리 중 오류가 발생했습니다", "error")
    }
  }

  clearCurrentNotification() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval)
      this.countdownInterval = null
    }
    if (this.currentNotification) {
      this.currentNotification.remove()
      this.currentNotification = null
    }
    // Stop the ringtone when notification is cleared
    this.stopNotificationSound()
  }

  playNotificationSound() {
    // Stop any existing ringtone
    this.stopNotificationSound()

    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()

      // Play ringtone pattern that repeats
      const playRing = () => {
        // First tone (higher pitch)
        const osc1 = audioContext.createOscillator()
        const gain1 = audioContext.createGain()
        osc1.connect(gain1)
        gain1.connect(audioContext.destination)
        osc1.frequency.value = 880
        osc1.type = 'sine'
        gain1.gain.setValueAtTime(0.3, audioContext.currentTime)
        gain1.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3)
        osc1.start(audioContext.currentTime)
        osc1.stop(audioContext.currentTime + 0.3)

        // Second tone (even higher pitch) - after short delay
        setTimeout(() => {
          const osc2 = audioContext.createOscillator()
          const gain2 = audioContext.createGain()
          osc2.connect(gain2)
          gain2.connect(audioContext.destination)
          osc2.frequency.value = 1100
          osc2.type = 'sine'
          gain2.gain.setValueAtTime(0.3, audioContext.currentTime)
          gain2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3)
          osc2.start(audioContext.currentTime)
          osc2.stop(audioContext.currentTime + 0.3)
        }, 200)

        // Third tone (back to first pitch) - completing the ring pattern
        setTimeout(() => {
          const osc3 = audioContext.createOscillator()
          const gain3 = audioContext.createGain()
          osc3.connect(gain3)
          gain3.connect(audioContext.destination)
          osc3.frequency.value = 880
          osc3.type = 'sine'
          gain3.gain.setValueAtTime(0.3, audioContext.currentTime)
          gain3.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3)
          osc3.start(audioContext.currentTime)
          osc3.stop(audioContext.currentTime + 0.3)
        }, 400)
      }

      // Play immediately
      playRing()

      // Repeat every 2 seconds for up to 30 seconds
      let repeatCount = 0
      const maxRepeats = 15
      this.ringtoneInterval = window.setInterval(() => {
        repeatCount++
        if (repeatCount >= maxRepeats) {
          this.stopNotificationSound()
          return
        }
        playRing()
      }, 2000)

    } catch (e) {
      console.error("[VoiceNotification] Failed to play sound:", e)
    }
  }

  stopNotificationSound() {
    if (this.ringtoneInterval) {
      clearInterval(this.ringtoneInterval)
      this.ringtoneInterval = null
    }
  }

  getCSRFToken(): string {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta?.getAttribute("content") || ""
  }

  escapeHtml(text: string): string {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}

// Global instance
const voiceNotificationManager = new VoiceNotificationManager()

// Initialize function
function initVoiceNotification() {
  const userMeta = document.querySelector('meta[name="current-user-id"]')
  if (userMeta) {
    // Always try to connect if not already connected
    if (!voiceNotificationManager.pusher) {
      console.log("[VoiceNotification] Initializing...")
      voiceNotificationManager.connect()
    } else {
      console.log("[VoiceNotification] Already connected, user:", voiceNotificationManager.userId)
    }
  } else {
    console.log("[VoiceNotification] No user logged in")
  }
}

// Make accessible globally immediately
;(window as any).voiceNotificationManager = voiceNotificationManager

console.log("[VoiceNotification] Module loaded")

// Try to connect immediately if DOM is already ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    console.log("[VoiceNotification] DOMContentLoaded fired")
    initVoiceNotification()
  })
} else {
  // DOM is already ready
  console.log("[VoiceNotification] DOM already ready, connecting now...")
  initVoiceNotification()
}

// Reconnect on Turbo navigation
document.addEventListener("turbo:load", () => {
  console.log("[VoiceNotification] turbo:load fired")
  initVoiceNotification()
})

// Also try on turbo:render for cached pages
document.addEventListener("turbo:render", () => {
  console.log("[VoiceNotification] turbo:render fired")
  initVoiceNotification()
})

export default voiceNotificationManager
